import type { ComponentProps, FC } from 'react';
import { CheckIcon } from 'lucide-react';
import DomainFavicon from '@/app/common/components/DomainFavicon';
import { domainMentionLabel } from '../utils/domainMentionMarkers';
import {
  McpMenuEmpty,
  McpMenuItem,
  McpMenuList,
  McpMenuSearchHeader
} from './McpSearchMenu';

export type McpUserDomain = {
  slug: string;
  url: string;
  show_name?: string | null;
};

export function filterDomains<T extends McpUserDomain>(
  domains: T[],
  query: string
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return domains;
  return domains.filter(d =>
    [d.url, d.show_name, d.slug]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(q)
  );
}

type McpDomainMenuBodyProps = Omit<
  ComponentProps<typeof McpMenuSearchHeader>,
  'title' | 'searchAriaLabel'
> & {
  domains: McpUserDomain[];
  totalCount: number;
  isLoading: boolean;
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  onSelect: (domain: McpUserDomain) => void;
  selectedSlug?: string | null;
};

/** Header (title + search) and searchable domain list. */
export const McpDomainMenuBody: FC<McpDomainMenuBodyProps> = ({
  domains,
  totalCount,
  isLoading,
  activeIndex,
  onActiveIndexChange,
  onSelect,
  selectedSlug,
  ...headerProps
}) => (
  <>
    <McpMenuSearchHeader
      title="دامنه‌ها"
      searchAriaLabel="جستجوی دامنه"
      {...headerProps}
    />
    {domains.length === 0 ? (
      <McpMenuEmpty>
        {isLoading
          ? 'در حال بارگذاری...'
          : totalCount === 0
            ? 'دامنه‌ای نیست'
            : 'دامنه‌ای پیدا نشد'}
      </McpMenuEmpty>
    ) : (
      <McpMenuList>
        {domains.map((domain, index) => {
          const address = domain.url?.trim() || 'دامنه';
          const showName = domain.show_name?.trim() || null;
          return (
            <McpMenuItem
              key={domain.slug}
              index={index}
              active={index === activeIndex}
              onActiveIndexChange={onActiveIndexChange}
              onSelect={() => onSelect(domain)}
              title={domainMentionLabel(domain)}
            >
              <DomainFavicon
                domain={domain.url}
                width={14}
                className="rounded-circle flex-shrink-0"
              />
              <span
                className="d-inline-flex align-items-center gap-1 min-w-0 flex-grow-1 overflow-hidden"
                dir="ltr"
              >
                <span className="fs--1 fw-medium text-truncate min-w-0">
                  {address}
                </span>
                {showName ? (
                  <span className="fs--2 text-600 text-truncate min-w-0">
                    ({showName})
                  </span>
                ) : null}
              </span>
              {selectedSlug === domain.slug ? (
                <CheckIcon
                  className="text-success flex-shrink-0"
                  size={12}
                  strokeWidth={2.5}
                />
              ) : null}
            </McpMenuItem>
          );
        })}
      </McpMenuList>
    )}
  </>
);
